import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Users, Building2, Landmark, Star, Phone } from 'lucide-react';
import { GalleryGrid } from '../components/GalleryGrid';
import { MOCK_SERVICES } from '../data';
import { imageUrl } from '../lib/images';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: { once: true }
};

export default function Home() {
  const [services, setServices] = useState(MOCK_SERVICES);

  useEffect(() => {
    async function fetchServices() {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (!response.ok) throw new Error('Failed to fetch services');
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setServices(data);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    }
    fetchServices();
  }, []);

  return (
    <div className="flex flex-col">
      <Helmet>
        <title>DK Grøn Service | Professionel Ejendomsservice & Grøn Vedligeholdelse</title>
        <meta name="description" content="Professionel ejendomsservice, græsslåning og grøn vedligeholdelse for boligforeninger, erhverv og private. Høj kvalitet, stabilitet og personlig kontakt. Få et uforpligtende tilbud!" />
        <meta property="og:title" content="DK Grøn Service | Professionel Ejendomsservice & Grøn Vedligeholdelse" />
        <meta property="og:description" content="Professionel ejendomsservice, græsslåning og grøn vedligeholdelse for boligforeninger, erhverv og private. Høj kvalitet, stabilitet og personlig kontakt." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/8a41795c-dcbb-4ba1-a6ae-e2582bf01d41/image00019.jpeg"
            alt="Velholdt grønt område"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-950/60 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="w-full space-y-8">
            <motion.div 
              className="inline-flex items-center gap-2 bg-emerald-600/20 backdrop-blur-md border border-emerald-500/30 px-4 py-2 rounded-full text-emerald-100 text-sm font-semibold"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Star className="w-4 h-4 fill-emerald-400 text-emerald-400" />
              Din lokale partner i ejendomsservice
            </motion.div>
            <motion.h1 
              className="w-full text-3xl md:text-5xl lg:text-7xl font-extrabold text-white leading-[1.1]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Professionel ejendomsservice med fokus på <span className="text-emerald-400">grønne områder</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-emerald-50/80 leading-relaxed max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Vi sørger for, at jeres udearealer altid fremstår præsentable og velplejede. Stabil service til private, erhverv og det offentlige.
            </motion.p>
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link
                to="/kontakt"
                className="bg-emerald-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-2xl font-bold text-base md:text-lg hover:bg-emerald-500 transition-all shadow-xl hover:shadow-emerald-500/20 flex items-center gap-2 group"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:+4542442359"
                className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-6 py-3 md:px-8 md:py-4 rounded-2xl font-bold text-base md:text-lg hover:bg-white/20 transition-all flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Ring op: 42 44 23 59
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-emerald-950 leading-tight">
                Lokalt firma med fokus på det gode håndværk
              </h2>
              <p className="text-lg text-emerald-800/70 leading-relaxed">
                DK Grøn Service er et dansk enkeltmandsfirma, der drives med en passion for velholdte udearealer. Jeg tror på, at personlig kontakt og stabil service er vejen til tilfredse kunder.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {[
                  'Altid personlig kontakt',
                  'Stabil og punktlig service',
                  'Fokus på kvalitet',
                  'Konkurrencedygtige priser',
                ].map((item, index) => (
                  <motion.div 
                    key={item} 
                    className="flex items-center gap-3 text-emerald-900 font-medium"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <div className="bg-red-50 p-1 rounded-full">
                      <CheckCircle2 className="w-5 h-5 text-red-600 shrink-0" />
                    </div>
                    {item}
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <div className="relative md:flex md:justify-center">
              <div className="relative w-full md:max-w-xs lg:max-w-full">
                <div className="aspect-[4/3] rounded-[2.5rem] overflow-hidden shadow-2xl">
                  <img
                    src="https://assets.auxodev.dk/assets/8a41795c-dcbb-4ba1-a6ae-e2582bf01d41/image00021.jpeg"
                    alt="DK Grøn Service i arbejde"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="absolute bottom-4 left-4 md:-bottom-6 md:-left-6 bg-emerald-600 text-white p-6 md:p-8 rounded-3xl shadow-xl overflow-hidden z-20">
                  {/* Background Image with Overlay */}
                  <div className="absolute inset-0 z-0">
                    <img 
                      src="https://assets.auxodev.dk/assets/8a41795c-dcbb-4ba1-a6ae-e2582bf01d41/image00019.jpeg" 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-emerald-900/60 backdrop-blur-[2px]" />
                  </div>

                  <div className="relative z-10">
                    <div className="text-3xl md:text-4xl font-bold mb-1">100%</div>
                    <div className="text-emerald-100 text-xs md:text-sm font-medium">Dansk ejet & drevet</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-emerald-50">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16 space-y-4"
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-emerald-950">Hvad kan vi hjælpe med?</h2>
            <p className="text-lg text-emerald-800/70">
              Vi tilbyder en bred vifte af opgaver inden for udendørs ejendomsservice og havepleje.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {services.map((service) => (
              <motion.div key={service.id} variants={fadeIn}>
                <Link
                  to={service.category === 'main' ? `/${service.url_slug}` : `/andre-opgaver/${service.url_slug}`}
                  className="group bg-white p-2 rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-300 border border-emerald-100 flex flex-col h-full relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-red-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                  <div className="aspect-[4/3] rounded-[1.5rem] overflow-hidden mb-6">
                    <img
                      src={imageUrl(Array.isArray(service.image) ? service.image[0] : service.image, 'width=600,quality=80,format=auto')}
                      loading="lazy"
                      decoding="async"
                      alt={service.title?.da || ''}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="px-4 pb-6 flex-grow">
                    <h3 className="text-xl font-bold text-emerald-950 mb-3 group-hover:text-emerald-600 transition-colors">
                      {service.title?.da || ''}
                    </h3>
                    <p className="text-emerald-800/60 text-sm line-clamp-3 mb-6">
                      {(service.description?.da || '').replace(/<[^>]*>/g, '').slice(0, 120)}...
                    </p>
                    <div className="flex items-center text-emerald-600 font-bold text-sm gap-2">
                      Læs mere
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Target Groups */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            <div className="text-center space-y-6 p-8 rounded-3xl hover:bg-emerald-50 transition-colors group">
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                <Users className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-950">Private</h3>
              <p className="text-emerald-800/60">
                Vi hjælper private haveejere med alt fra græsslåning til løbende vedligeholdelse af haven.
              </p>
            </div>
            <div className="text-center space-y-6 p-8 rounded-3xl hover:bg-emerald-50 transition-colors group">
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                <Building2 className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-950">Virksomheder</h3>
              <p className="text-emerald-800/60">
                Professionel pasning af grønne arealer ved kontorer, lagerhaller og boligforeninger.
              </p>
            </div>
            <div className="text-center space-y-6 p-8 rounded-3xl hover:bg-emerald-50 transition-colors group md:col-span-2 lg:col-span-1">
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                <Landmark className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-950">Kommuner</h3>
              <p className="text-emerald-800/60">
                Vi løser mindre opgaver for kommuner og offentlige institutioner med fokus på stabilitet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 bg-emerald-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-2xl space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold text-emerald-950">Udført arbejde</h2>
              <p className="text-lg text-emerald-800/70">
                Se et udpluk af de opgaver vi har løst for vores kunder. Vi sætter en ære i det færdige resultat.
              </p>
            </div>
            <Link
              to="/galleri"
              className="bg-white text-emerald-900 px-8 py-4 rounded-2xl font-bold border border-emerald-100 hover:bg-emerald-100 transition-all flex items-center gap-2 shrink-0 shadow-sm"
            >
              Se hele galleriet
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <GalleryGrid limit={3} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-emerald-900 rounded-[3rem] overflow-hidden relative shadow-2xl shadow-emerald-950/20">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
              <div className="p-10 md:p-16 lg:p-20 relative z-10">
                <span className="inline-block px-4 py-1.5 bg-emerald-800 text-emerald-400 text-xs font-bold tracking-widest uppercase rounded-full mb-6">
                  Klar til en flottere have?
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                  Skal vi tage os af dine grønne arealer?
                </h2>
                <p className="text-emerald-100 text-lg mb-10 max-w-xl leading-relaxed">
                  Kontakt os i dag for en uforpligtende snak om dine behov. Vi vender tilbage med et skarpt tilbud inden for 24 timer.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <Link 
                    to="/kontakt" 
                    className="w-full sm:w-auto bg-white text-emerald-900 px-8 py-4 rounded-full font-bold hover:bg-emerald-50 transition-all hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/20"
                  >
                    Kontakt os nu
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:42442359" 
                    className="w-full sm:w-auto bg-emerald-800/50 backdrop-blur-sm border border-emerald-700 text-white px-8 py-4 rounded-full font-bold hover:bg-emerald-800 transition-all hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    42 44 23 59
                  </a>
                </div>
              </div>
              <div className="relative h-full min-h-[400px] hidden lg:block">
                <img 
                  src="https://assets.auxodev.dk/assets/8a41795c-dcbb-4ba1-a6ae-e2582bf01d41/image00002.jpeg" 
                  alt="DK Grøn Service Arbejdsbil" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-900 via-emerald-900/20 to-transparent" />
                
                {/* Floating Trust Badge */}
                <div className="absolute bottom-10 right-10 bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl text-white max-w-[220px]">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-red-400">Hurtig Service</span>
                  </div>
                  <p className="text-sm font-medium leading-snug">Vi svarer altid på din henvendelse inden for 24 timer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
